// `/tickets/drafts/team`: every agent's open drafts, for support leads and above
// (docs/superpowers/specs/2026-10-13-multi-ticket-drafts-design.md, "Team drafts"). One row
// per draft, oldest first as the server sends them: owner (flagged "Deactivated" in words
// when the person is gone), store, subject, age, last saved. Contents stay hidden until a
// take-over; the list carries none (docs/API.md §12.6).
//
// "Take over" asks first, in the shared Modal, then moves the draft to the signed-in lead
// (§12.7), refreshes the lead's own tab row so the draft is in it, and opens the draft. A
// draft that is no longer open (filed or discarded) answers 404: the list is read again, a
// notice says so, and focus moves to it (the row the dialog would return focus to is gone).
// A draft another lead has already taken is still open, so a stale row lets a lead move a
// draft from another lead; the spec allows that.
//
// A lead's own drafts are listed too, with "Open" instead of "Take over": taking over your
// own draft is a no-op on the server, and asking "take over your own draft?" would be odd.

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router';

import { api } from '../../api/client.js';
import Modal from '../../components/Modal.jsx';
import { useDrafts } from '../../components/drafts/DraftsProvider.jsx';
import { TEAM_DRAFTS_CHANGED } from '../../components/drafts/TeamDraftsLink.jsx';
import { draftHref } from '../../components/drafts/draft-address.js';
import { agoText, storeText, takeOverPrompt } from '../../components/drafts/team-drafts.js';
import { useSession } from '../../session/SessionProvider.jsx';

// The ages are worked out against this clock, so it ticks once a minute, as the tab row's does.
const AGE_TICK_MS = 60 * 1000;

// A busy control keeps focus and looks unavailable; the handler is what refuses the click.
const BUSY_LOOK = 'aria-disabled:cursor-not-allowed aria-disabled:opacity-50';

export default function TeamDrafts() {
  const user = useSession();
  const navigate = useNavigate();
  const { refresh } = useDrafts();
  const [list, setList] = useState({ status: 'loading', rows: [] });
  const [now, setNow] = useState(() => Date.now());
  const [notice, setNotice] = useState(null);
  // Bumped once the list has been re-read after a 404; the effect below then focuses the notice.
  const [focusNotice, setFocusNotice] = useState(0);
  const noticeRef = useRef(null);
  // The draft the confirmation is about, or null when it is closed.
  const [confirming, setConfirming] = useState(null);
  const [busy, setBusy] = useState(false);
  const [modalError, setModalError] = useState(null);
  const mounted = useRef(true);
  const cancelRef = useRef(null);

  // Reads the list. A failed read keeps what the page already showed, unless there was
  // nothing yet. A 403 is its own state: this person is not a lead, whatever the link said.
  const load = useCallback(async () => {
    try {
      const response = await api.get('/ticket-drafts/team');
      if (!mounted.current) return;
      const rows = Array.isArray(response?.data) ? response.data : [];
      setList({ status: 'ready', rows });
      // Hands the header's link the count, so it need not read the list a second time.
      window.dispatchEvent(new CustomEvent(TEAM_DRAFTS_CHANGED, { detail: { count: rows.length } }));
    } catch (error) {
      if (!mounted.current) return;
      if (error?.status === 403) setList({ status: 'forbidden', rows: [] });
      else setList((previous) => (previous.status === 'ready' ? previous : { status: 'error', rows: [] }));
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    void load();
    const timer = setInterval(() => setNow(Date.now()), AGE_TICK_MS);
    window.addEventListener('focus', load);
    return () => {
      mounted.current = false;
      clearInterval(timer);
      window.removeEventListener('focus', load);
    };
  }, [load]);

  useEffect(() => {
    if (focusNotice > 0) noticeRef.current?.focus();
  }, [focusNotice]);

  function closeConfirm() {
    if (busy) return;
    setConfirming(null);
    setModalError(null);
  }

  async function confirmTakeOver() {
    if (busy || !confirming) return;
    const draft = confirming;
    setBusy(true);
    setModalError(null);
    try {
      await api.post(`/ticket-drafts/${draft.id}/take-over`, {});
      // The draft is the lead's now: read their tab row so it is there when the screen opens.
      await refresh();
      if (!mounted.current) return;
      navigate(draftHref(draft.id));
    } catch (error) {
      if (!mounted.current) return;
      setBusy(false);
      if (error?.status === 404) {
        setConfirming(null);
        setNotice('That draft was filed or discarded. The list is up to date.');
        // The dialog returns focus to the row's button as it closes, and this read removes
        // that row, so focus would fall to the page. Focus the notice once the list is read.
        await load();
        if (mounted.current) setFocusNotice((n) => n + 1);
      } else {
        setModalError(error?.message || 'The draft could not be taken over. Try again.');
      }
    }
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-lg font-semibold text-ink">Team drafts</h1>
        <p className="mt-1 text-sm text-ink-muted">
          Tickets people have started and not filed, oldest first. Their contents stay private
          until you take one over.
        </p>
      </div>

      {notice && (
        <div
          ref={noticeRef}
          tabIndex={-1}
          role="status"
          className="flex items-start justify-between gap-3 rounded-md border border-line bg-sunken px-3 py-2 text-sm text-ink focus:outline-none focus-visible:outline-2 focus-visible:outline-brand-600 dark:focus-visible:outline-brand-400"
        >
          <span>{notice}</span>
          <button type="button" className="btn-ghost px-2 py-0.5 text-xs" onClick={() => setNotice(null)}>
            Dismiss
          </button>
        </div>
      )}

      {list.status === 'forbidden' && (
        <p role="alert" className="rounded-lg border border-line bg-surface p-4 text-sm text-ink-secondary">
          Only support leads can see everyone&rsquo;s drafts.
        </p>
      )}

      {list.status === 'error' && (
        <p role="alert" className="rounded-lg border border-line bg-surface p-4 text-sm text-ink-secondary">
          The team&rsquo;s drafts could not be loaded.{' '}
          <button type="button" className="font-medium text-brand-700 hover:underline dark:text-brand-300" onClick={load}>
            Try again
          </button>
        </p>
      )}

      {(list.status === 'loading' || list.status === 'ready') && (
        <div className="overflow-x-auto rounded-lg border border-line bg-surface">
          <table className="w-full min-w-[48rem] divide-y divide-line text-sm">
            <caption className="sr-only">Open drafts, oldest first</caption>
            <thead className="bg-sunken text-left text-xs font-medium uppercase tracking-wide text-ink-secondary">
              <tr>
                <th scope="col" className="px-4 py-2.5">Owner</th>
                <th scope="col" className="px-4 py-2.5">Store</th>
                <th scope="col" className="px-4 py-2.5">Subject</th>
                <th scope="col" className="px-4 py-2.5">Started</th>
                <th scope="col" className="px-4 py-2.5">Last saved</th>
                <th scope="col" className="px-4 py-2.5"><span className="sr-only">Action</span></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {list.status === 'loading' && (
                <tr><td colSpan={6} className="px-4 py-6 text-center text-ink-muted">Loading…</td></tr>
              )}
              {list.status === 'ready' && list.rows.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-6 text-center text-ink-muted">No open drafts.</td></tr>
              )}
              {list.rows.map((draft) => {
                const mine = String(draft.owner?.id) === String(user.id);
                const ownerName = draft.owner?.display_name || 'Unknown';
                const started = draft.started_by?.display_name;
                return (
                  <tr key={draft.id} className="transition-colors hover:bg-sunken">
                    <td className="px-4 py-2.5 align-top text-ink">
                      <span className="font-medium">{ownerName}</span>
                      {/* In words, not colour alone. */}
                      {draft.owner?.is_active === false && (
                        <span className="badge badge-danger ml-2">Deactivated</span>
                      )}
                      {started && started !== ownerName && (
                        <div className="text-xs text-ink-muted">started by {started}</div>
                      )}
                    </td>
                    <td className="px-4 py-2.5 align-top text-ink-secondary">{storeText(draft)}</td>
                    <td className="max-w-xs px-4 py-2.5 align-top text-ink-secondary">
                      {draft.subject?.trim() ? (
                        <span className="break-words">{draft.subject}</span>
                      ) : (
                        <span className="text-ink-muted">(no subject)</span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 align-top tabular-nums text-ink-secondary">
                      {agoText(draft.created_at, now)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 align-top tabular-nums text-ink-secondary">
                      {agoText(draft.updated_at, now)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-right align-top">
                      {mine ? (
                        <Link to={draftHref(draft.id)} className="btn-ghost px-2 py-1 ring-1 ring-line-strong">
                          Open
                        </Link>
                      ) : (
                        <button
                          type="button"
                          aria-label={`Take over ${ownerName}'s draft: ${draft.subject?.trim() || 'no subject'}`}
                          className={`btn-ghost px-2 py-1 ring-1 ring-line-strong ${BUSY_LOOK}`}
                          aria-disabled={busy || undefined}
                          onClick={() => {
                            if (busy) return;
                            setModalError(null);
                            setConfirming(draft);
                          }}
                        >
                          Take over
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {confirming && (
        // Focus lands on Cancel, the safe answer, so Enter on a freshly opened dialog
        // takes nothing over.
        <Modal title="Take over this draft?" onClose={closeConfirm} initialFocusRef={cancelRef}>
          <div className="space-y-4 px-4 py-4">
            <p className="text-sm text-ink">{takeOverPrompt(confirming.owner)}</p>
            {modalError && (
              <p role="alert" className="text-sm text-rose-700 dark:text-rose-300">{modalError}</p>
            )}
            <div className="flex justify-end gap-2">
              <button
                ref={cancelRef}
                type="button"
                className={`btn-ghost ring-1 ring-line-strong ${BUSY_LOOK}`}
                aria-disabled={busy || undefined}
                onClick={closeConfirm}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`btn-primary ${BUSY_LOOK}`}
                aria-disabled={busy || undefined}
                onClick={confirmTakeOver}
              >
                {busy ? 'Taking over…' : 'Take over'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
